import React, { useEffect, useState } from "react";
import { catalogApi } from "../api/client";
import { useLocation } from "react-router-dom";
import { SearchX } from "lucide-react";
import ListingCard from "./ListingCard";

export default function ApiServiceGrid() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const location = useLocation();

  const run = async (pageNum = 1) => {
    try {
      setLoading(true);
      const params = Object.fromEntries(new URLSearchParams(location.search));

      // keep original query structure
      const query = { page: pageNum, limit: 12, sort: "created_desc" };
      if (params.q) query.q = params.q;
      if (params.categoryIds) query.categoryIds = params.categoryIds;
      if (params.subcategoryIds) query.subcategoryIds = params.subcategoryIds;
      if (params.lat && params.lng) {
        query.lat = Number(params.lat);
        query.lng = Number(params.lng);
      }
      if (params.radiusKm) query.radiusKm = Number(params.radiusKm);
      if (params.priceMin) query.priceMin = Number(params.priceMin);
      if (params.priceMax) query.priceMax = Number(params.priceMax);
      if (params.companyType) query.companyType = params.companyType;

      const data = await catalogApi.publicSearch(query);
      const newItems = Array.isArray(data?.items) ? data.items : [];

      if (pageNum === 1) {
        setItems(newItems);
      } else {
        setItems((prev) => [...prev, ...newItems]);
      }

      setHasMore(newItems.length >= 12); // check if more pages exist
    } catch (e) {
      setError("Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setItems([]); // 👈 clear old results first
    run(1);
  }, [location.search]);

  if (loading && page === 1)
    return <div className="text-center text-white">Loading services...</div>;

  if (error) return <div className="text-center text-red-500">{error}</div>;

  if (!loading && !items.length)
    return (
      <div className="flex flex-col items-center justify-center text-gray-700 bg-white/60 rounded-md p-10 border border-dashed">
        <SearchX className="h-10 w-10 text-gray-400 mb-2" />
        <div className="text-lg font-medium">No services found</div>
        <div className="text-sm text-gray-500">
          Try adjusting your search or filters.
        </div>
      </div>
    );

  return (
    <div className="flex flex-col items-center w-full">
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 w-full">
        {items.map((listing) => (
          <ListingCard key={listing.id || listing._id} listing={listing} />
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => {
            const nextPage = page + 1;
            setPage(nextPage);
            run(nextPage);
          }}
          disabled={loading}
          className="mt-6 px-3 py-1 md:px-6 md:py-2 text-sm md:text-base rounded-md md:rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Loading..." : "Load More"}
        </button>
      )}
    </div>
  );
}
